import { createSlice } from '@reduxjs/toolkit';
import * as APIt from '../../API';
import { debug } from '../../utils/commonUtils';

export type Search = {
  maskStatus: { label: string, value: string } | undefined,
  modes: { label: string, value: string }[] | undefined,
  savedSearch: { label: string, value: string } | undefined,
  textFilter: string | undefined,
};
export interface DevicesSliceInterface {
  inputs: APIt.ChildDevice[];
  inputsSearch: Search | undefined;
  loadingDevices: boolean,
  readersAlarms: APIt.ChildDevice[];
  readersModes: APIt.ChildDevice[];
  readersAlarmsSearch: Search | undefined;
  readersModesSearch: Search | undefined;
  selectedInputs: APIt.ChildDevice[];
  selectedReadersAlarms: APIt.ChildDevice[];
  selectedReadersModes: APIt.ChildDevice[];
  textFilter: string | null;
};

export const initialDevicesState: DevicesSliceInterface = {
  inputs: [],
  inputsSearch: undefined,
  loadingDevices: false,
  readersAlarms: [],
  readersModes: [],
  readersAlarmsSearch: undefined,
  readersModesSearch: undefined,
  selectedInputs: [],
  selectedReadersAlarms: [],
  selectedReadersModes: [],
  textFilter: null,
};

export const devicesSlice = createSlice({
  name: 'devicesState',
  initialState: initialDevicesState,
  reducers: {
    setInputsDevices: ( state, action: { type: string, payload: { inputs: APIt.ChildDevice[] } }) => {
      state.inputs = action.payload.inputs;
    },
    setReadersAlarmsDevices: ( state, action: { type: string, payload: { readersAlarms: APIt.ChildDevice[] } }) => {
      state.readersAlarms = action.payload.readersAlarms;
    },
    setReadersModesDevices: ( state, action: { type: string, payload: { readersModes: APIt.ChildDevice[] } }) => {
      state.readersModes = action.payload.readersModes;
    },
    setInputsSearch: (state, action: { type: string, payload: Search | undefined } ) => {
      state.inputsSearch = action.payload;
    },
    setLoadingDevices: (state, action: { type: string, payload: boolean } ) => {
      state.loadingDevices = action.payload;
    },
    setReadersAlarmsSearch: (state, action: { type: string, payload: Search | undefined } ) => {
      state.readersAlarmsSearch = action.payload;
    },
    setReadersModesSearch: (state, action: { type: string, payload: Search | undefined } ) => {
      state.readersModesSearch = action.payload;
    },
    setSelectedInputs: (state, action: { type: string, payload: APIt.ChildDevice[] } ) => {
      debug(`setSelectedInputs() action is ${JSON.stringify(action)}`);
      state.selectedInputs = action.payload;
    },
    setSelectedReadersAlarms: (state, action: { type: string, payload: APIt.ChildDevice[] } ) => {
      state.selectedReadersAlarms = action.payload;
    },
    setSelectedReadersModes: (state, action: { type: string, payload: APIt.ChildDevice[] } ) => {
      state.selectedReadersModes = action.payload;
    },
    updateInputMasked: (state, action: { type: string, payload: APIt.ChildDevice } ) => {
      debug(`updateInputMasked() action is ${JSON.stringify(action)}`);
      const updatedDevice = action.payload as APIt.ChildDevice;
      state.inputs.forEach((e: APIt.ChildDevice, i) => {
        if (e.DeviceName === updatedDevice.DeviceName) state.inputs[i].Masked = updatedDevice.Masked;
      });
      debug(`updateInputStatus() state.readersAlarms is ${JSON.stringify(state.readersAlarms)}`);
    },
    updateReaderMasked: (state, action: { type: string, payload: APIt.ChildDevice } ) => {
      debug(`updateReaderMasked() action is ${JSON.stringify(action)}`);
      const updatedDevice = action.payload as APIt.ChildDevice;
      state.readersAlarms.forEach((e: APIt.ChildDevice, i) => {
        if (e.DeviceName === updatedDevice.DeviceName) {
          state.readersAlarms[i].DoorForcedMasked = updatedDevice.DoorForcedMasked;
          state.readersAlarms[i].DoorHeldMasked = updatedDevice.DoorHeldMasked;
        }
      });
      debug(`updateReaderMasked() state.readersAlarms is ${JSON.stringify(state.readersAlarms)}`);
    },
    updateReaderMode: (state, action: { type: string, payload: APIt.ChildDevice } ) => {
      debug(`updateReaderMode() action is ${JSON.stringify(action)}`);
      const updatedDevice = action.payload;
      state.readersModes.forEach((e, i) => {
        if (e.DeviceName === updatedDevice.DeviceName) state.readersModes[i].Mode = updatedDevice.Mode;
      });
      debug(`updateReaderMode() state.readersModes is ${JSON.stringify(state.readersModes)}`);
    },
    updateSelectedInputMask: (state, action: { type: string, payload: APIt.ChildDevice } ) => {
      debug('updateSelectedInputMask()');
      debug(`updateSelectedInputMask() action is ${JSON.stringify(action)}`);
      const updatedDevice = action.payload;
      state.selectedInputs.forEach((e, i) => {
        if (e.DeviceName === updatedDevice.DeviceName) state.selectedInputs[i].Masked = updatedDevice.Masked;
      });
      debug(`updateSelectedInputMask() state.selectedInputs is ${JSON.stringify(state.selectedInputs)}`);
    },
    updateSelectedReaderMasked: (state, action: { type: string, payload: APIt.ChildDevice } ) => {
      debug(`updateSelectedReaderAlarmDoorForcedMasked() action is ${JSON.stringify(action)}`);
      const updatedDevice = action.payload;
      state.selectedReadersAlarms.forEach((e, i) => {
        if (e.DeviceName === updatedDevice.DeviceName) {
          state.selectedReadersAlarms[i].DoorForcedMasked = updatedDevice.DoorForcedMasked;
          state.selectedReadersAlarms[i].DoorHeldMasked = updatedDevice.DoorHeldMasked;
        }
      });
      debug(`updateSelectedReaderAlarmDoorForcedMasked() state.selectedReadersAlarms is ${JSON.stringify(state.selectedReadersAlarms)}`);
    },
    updateSelectedReaderMode: (state, action: { type: string, payload: APIt.ChildDevice } ) => {
      debug(`updateSelectedReaderMode() action is ${JSON.stringify(action)}`);
      const updatedDevice = action.payload;
      state.selectedReadersModes.forEach((e, i) => {
        if (e.DeviceName === updatedDevice.DeviceName) state.selectedReadersModes[i].Mode = updatedDevice.Mode;
      });
      debug(`updateSelectedReaderMode() state.selectedReadersModes is ${JSON.stringify(state.selectedReadersModes)}`);
    },
  },
});

export const {
  setInputsDevices,
  setInputsSearch,
  setLoadingDevices,
  setReadersAlarmsDevices,
  setReadersAlarmsSearch,
  setReadersModesDevices,
  setReadersModesSearch,
  setSelectedInputs,
  setSelectedReadersAlarms,
  setSelectedReadersModes,
  updateInputMasked,
  updateReaderMasked,
  updateReaderMode,
  updateSelectedInputMask,
  updateSelectedReaderMasked,
  updateSelectedReaderMode
} = devicesSlice.actions;

export const selectDevices = (state: any) => state.devices;
export const selectInputDevices = (state: any) => state.devices.inputs;
export const selectInputsSearch = (state: any) => state.devices.inputsSearch;
export const selectReadersAlarmsDevices = (state: any) => state.devices.readersAlarms;
export const selectReadersAlarmsSearch = (state: any) => state.devices.readersAlarmsSearch;
export const selectReadersModesDevices = (state: any) => state.devices.readersModes;
export const selectReadersModesSearch = (state: any) => state.devices.readersModesSearch;
export const selectSelectedInputs = (state: any) => state.devices.selectedInputs;
export const selectSelectedReadersAlarms = (state: any) => state.devices.selectedReadersAlarms;
export const selectSelectedReadersModes = (state: any) => state.devices.selectedReadersModes;
export const selectLoadingDevices = (state: any) => state.loadingDevices;

export default devicesSlice.reducer;